import {
    ButtonGradientPrimary,
    ButtonSecondary,
    ConfirmDialog,
    Icon,
    IconPicker,
    type IconPickerOption,
    TextInput,
    Colors,
} from '@amelio/ui-library';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Trash2Icon } from '../icons';
import { CARD_SURFACE } from './colorPresets';
import { EmployeePortalCompetenciesSection } from './EmployeePortalCompetenciesSection';
import { LevelIconPreview } from './LevelIconPreview';
import { PerformanceScoreLevelsSection } from './PerformanceScoreLevelsSection';

import type { TranslationKey } from '@/i18n-key';
import type { ProficiencyLevelDefinition } from '@/types/competency/ProficiencyLevelDefinition';

import {
    useGetProficiencyLevels,
    useReplaceProficiencyLevels,
} from '@/api/competency-services/proficiency-level';
import LevelColorPicker from '@/components/LevelColorPicker/LevelColorPicker';
import { PROFICIENCY_ICON_CATALOG } from '@/constants/proficiencyIconCatalog';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

const MIN_LEVELS = 3;
const MAX_LEVELS = 6;
const CUSTOM_ICON_VALUE = 'custom';
const MAX_CUSTOM_ICON_BYTES = 100 * 1024;
const MAX_LEVEL_NAME_LENGTH = 100;

type WorkingLevel = {
    key: string;
    id?: string | null;
    icon: string;
    iconUrl?: string | null;
    iconColor?: string | null;
    label: Record<string, string>;
};

// Same four levels as `ProficiencyLevelSeeder` on the server, and in the same order:
// best first, because that is how the API returns them (SortOrder 0 is the highest) and
// this list stands in for that answer. It used to run worst-first, so the fallback showed
// the scale upside down compared with every company that had saved one.
//
// The words come from the icon catalogue rather than from a second set of keys, so the
// name shown here is the name shown in the picker when someone edits the level.
const DEFAULT_LEVELS: { icon: string; labelKey: TranslationKey }[] = [
    { icon: 'achieved', labelKey: 'competencies.levels.icons.achieved' },
    { icon: 'almostAchieved', labelKey: 'competencies.levels.icons.almostAchieved' },
    { icon: 'evolution', labelKey: 'competencies.levels.icons.evolution' },
    { icon: 'notMet', labelKey: 'competencies.levels.icons.notMet' },
];

let keyCounter = 0;
const nextKey = () => `level-${keyCounter++}`;

const toWorking = (levels: ProficiencyLevelDefinition[]): WorkingLevel[] =>
    levels.map((level) => ({
        key: nextKey(),
        id: level.id,
        icon: level.icon,
        iconUrl: level.iconUrl,
        iconColor: level.iconColor,
        label: level.label,
    }));

const seedDefaults = (t: (key: TranslationKey) => string, language: string): WorkingLevel[] =>
    DEFAULT_LEVELS.map(({ icon, labelKey }) => ({
        icon,
        label: { [language]: t(labelKey) },
        key: nextKey(),
    }));

export const ProficiencyLevelsTab = () => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { data, isLoading, isError, refetch } = useGetProficiencyLevels();
    const replaceMutation = useReplaceProficiencyLevels();

    const iconOptions = useMemo<IconPickerOption[]>(
        () =>
            PROFICIENCY_ICON_CATALOG.map((entry) => ({
                value: entry.value,
                label: t(entry.labelKey),
                preview: (
                    <svg
                        className={`h-5 w-5 ${entry.colorClass}`}
                        viewBox={'0 0 24 24'}
                        stroke={'currentColor'}
                    >
                        {entry.paths}
                    </svg>
                ),
            })),
        [t],
    );

    const [levels, setLevels] = useState<WorkingLevel[]>([]);
    const [savedLevels, setSavedLevels] = useState<WorkingLevel[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const uploadIndexRef = useRef<number | null>(null);
    const [pendingAdd, setPendingAdd] = useState(false);
    const [levelToRemove, setLevelToRemove] = useState<number | null>(null);
    const [touchedNameKeys, setTouchedNameKeys] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!data) {
            return;
        }

        const hydrated = data.length > 0 ? toWorking(data) : seedDefaults(t, language);
        setLevels(hydrated);
        setSavedLevels(hydrated);
        setTouchedNameKeys(new Set());
    }, [data, language]);

    const hasChanges = useMemo(() => {
        const strip = (list: WorkingLevel[]) =>
            list.map(({ icon, iconUrl, iconColor, label }) => ({
                icon,
                iconUrl: iconUrl ?? null,
                iconColor: iconColor ?? null,
                label,
            }));
        return JSON.stringify(strip(levels)) !== JSON.stringify(strip(savedLevels));
    }, [levels, savedLevels]);

    const invalidNameKeys = useMemo(() => {
        const keys = new Set<string>();
        levels.forEach((level) => {
            if (!getTextByLanguage(level.label, language).trim()) {
                keys.add(level.key);
            }
        });
        return keys;
    }, [levels, language]);

    const hasInvalidName = invalidNameKeys.size > 0;

    const updateLevel = (index: number, patch: Partial<WorkingLevel>) =>
        setLevels((current) =>
            current.map((level, i) => (i === index ? { ...level, ...patch } : level)),
        );

    const markLevelNameAsTouched = (key: string) => {
        setTouchedNameKeys((current) => {
            const next = new Set(current);
            next.add(key);
            return next;
        });
    };

    const addLevel = () => {
        if (levels.length >= MAX_LEVELS) {
            return;
        }

        setLevels((current) => [...current, { key: nextKey(), icon: 'evolution', label: {} }]);
    };

    const triggerUpload = (index: number) => {
        uploadIndexRef.current = index;
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const index = uploadIndexRef.current;
        if (!file || index === null) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            showErrorToast({ message: t('competencies.levels.uploadInvalidType') });
            return;
        }

        if (file.size > MAX_CUSTOM_ICON_BYTES) {
            showErrorToast({ message: t('competencies.levels.uploadTooLarge') });
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = typeof reader.result === 'string' ? reader.result : '';
            if (dataUrl) {
                updateLevel(index, { icon: CUSTOM_ICON_VALUE, iconUrl: dataUrl });
            }

            uploadIndexRef.current = null;
        };
        reader.onerror = () => showErrorToast({ message: t('competencies.levels.uploadError') });
        reader.readAsDataURL(file);
    };

    const saveLevels = (list: WorkingLevel[]) => {
        const hasEmptyName = list.some((level) => !getTextByLanguage(level.label, language).trim());
        if (hasEmptyName) {
            showErrorToast({ message: t('competencies.levels.nameRequired') });
            return;
        }

        replaceMutation.mutate(
            {
                levels: list.map((level, index) => ({
                    id: level.id ?? null,
                    icon: level.icon,
                    iconUrl: level.iconUrl ?? null,
                    iconColor: level.iconColor ?? null,
                    label: level.label,
                    sortOrder: index,
                })),
            },
            {
                onSuccess: (saved) => {
                    const hydrated = toWorking(saved);
                    setLevels(hydrated);
                    setSavedLevels(hydrated);
                    setTouchedNameKeys(new Set());
                    showSuccessToast({ message: t('competencies.levels.saved') });
                },
                onError: () => showErrorToast({ message: t('competencies.levels.saveError') }),
            },
        );
    };

    const handleSave = () => saveLevels(levels);

    const handleReset = () => {
        setLevels(savedLevels);
        setTouchedNameKeys(new Set());
    };

    if (isLoading) {
        return (
            <div className={CARD_SURFACE}>
                <p className={'py-8 text-center text-sm text-[#5E5E5E]'}>
                    {t('competencies.levels.loading')}
                </p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className={CARD_SURFACE}>
                <div className={'flex flex-col items-center gap-4 py-8 text-center'}>
                    <p className={'text-sm text-[#ef4444]'}>{t('competencies.levels.loadError')}</p>

                    <button
                        type={'button'}
                        onClick={() => refetch()}
                        className={
                            'text-navy flex h-11 items-center rounded-lg border border-gray-300 bg-white px-6 text-sm font-medium transition-colors hover:bg-[#f5f7f9]'
                        }
                    >
                        {t('competencies.levels.retry')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`${CARD_SURFACE} !p-[44px]`}>
            <div className={'mb-8'}>
                <h2 className={'text-navy text-subtitle mb-2 font-semibold'}>
                    {t('competencies.levels.title')}
                </h2>

                <p className={'text-navy/60 text-sm'}>
                    {t('competencies.levels.subtitle', { min: MIN_LEVELS, max: MAX_LEVELS })}
                </p>
            </div>

            <div className={'flex max-w-2xl flex-col gap-3'}>
                {levels.map((level, index) => (
                    <div
                        key={level.key}
                        className={'flex items-start gap-3'}
                    >
                        <IconPicker
                            options={iconOptions}
                            value={level.icon}
                            selectedPreview={
                                <LevelIconPreview
                                    icon={level.icon}
                                    iconUrl={level.iconUrl}
                                    iconColor={level.iconColor}
                                />
                            }
                            onSelect={(icon) => updateLevel(index, { icon, iconUrl: null })}
                            importLabel={
                                <>
                                    <Icon
                                        name={'arrow-export'}
                                        size={'sm'}
                                        colorClassName={'text-primary'}
                                    />{' '}
                                    {t('competencies.levels.importIcon')}
                                </>
                            }
                            onImport={() => triggerUpload(index)}
                            className={'h-[40px] border-gray-200'}
                        />

                        <LevelColorPicker
                            value={level.iconColor ?? null}
                            onChange={(iconColor) => updateLevel(index, { iconColor })}
                        />

                        <div className={'flex-1'}>
                            <TextInput
                                value={getTextByLanguage(level.label, language)}
                                placeholder={t('competencies.levels.labelPlaceholder')}
                                onChange={(value) => {
                                    if (value.length > MAX_LEVEL_NAME_LENGTH) {
                                        showErrorToast({
                                            message: t('competencies.levels.nameTooLong'),
                                        });
                                        return;
                                    }
                                    markLevelNameAsTouched(level.key);
                                    updateLevel(index, {
                                        label: {
                                            ...level.label,
                                            [language]: value,
                                        },
                                    });
                                }}
                                variant={'white'}
                                bordered={true}
                                error={
                                    invalidNameKeys.has(level.key) && touchedNameKeys.has(level.key)
                                }
                                errorMessage={t('competencies.levels.nameRequired')}
                                className={'h-[40px] border-gray-200'}
                                dataTestId={`level-label-${index}`}
                            />
                        </div>

                        <button
                            type={'button'}
                            onClick={() => setLevelToRemove(index)}
                            disabled={levels.length <= MIN_LEVELS}
                            aria-label={t('competencies.levels.remove')}
                            className={
                                'mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#5E5E5E] transition-colors hover:bg-[#fef2f2] hover:text-[#ef4444] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#5E5E5E]'
                            }
                        >
                            <Trash2Icon className={'h-4 w-4'} />
                        </button>
                    </div>
                ))}
            </div>

            <input
                ref={fileInputRef}
                type={'file'}
                accept={'image/*'}
                className={'hidden'}
                onChange={handleFileSelected}
            />

            <button
                type={'button'}
                onClick={() => setPendingAdd(true)}
                disabled={levels.length >= MAX_LEVELS}
                className={
                    'text-navy mt-4 flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 text-sm font-medium transition-colors hover:bg-[#f5f7f9] disabled:cursor-not-allowed disabled:opacity-40'
                }
            >
                <span className={'text-lg leading-none'}>+</span>

                {t('competencies.levels.addLevel')}
            </button>

            <div className={'mt-8 flex items-center gap-3'}>
                <ButtonGradientPrimary
                    size={'md'}
                    onClick={handleSave}
                    disabled={!hasChanges || hasInvalidName || replaceMutation.isPending}
                    dataTestId={'levels-save'}
                    leftIcon={
                        <Icon
                            name={'archive-save-add'}
                            size={'md'}
                            color={Colors.WHITE}
                        />
                    }
                >
                    {replaceMutation.isPending
                        ? t('competencies.levels.saving')
                        : t('competencies.levels.save')}
                </ButtonGradientPrimary>

                {hasChanges && (
                    <ButtonSecondary
                        onClick={handleReset}
                        size={'md'}
                        className={'sm:text-md text-sm'}
                        fitContent={true}
                        leftIcon={
                            <Icon
                                name={'arrow-refresh'}
                                size={'md'}
                            />
                        }
                    >
                        {t('competencies.levels.cancel')}
                    </ButtonSecondary>
                )}
            </div>

            <ConfirmDialog
                isOpen={pendingAdd}
                title={t('competencies.levels.addConfirm.title')}
                message={t('competencies.levels.addConfirm.message')}
                confirmLabel={t('competencies.levels.addConfirm.confirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={() => {
                    addLevel();
                    setPendingAdd(false);
                }}
                onCancel={() => setPendingAdd(false)}
            />

            <ConfirmDialog
                isOpen={levelToRemove !== null}
                title={t('competencies.levels.removeConfirm.title')}
                message={t('competencies.levels.removeConfirm.message')}
                confirmLabel={t('competencies.levels.removeConfirm.confirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={() => {
                    if (levelToRemove === null) {
                        return;
                    }

                    const nextLevels = levels.filter((_, i) => i !== levelToRemove);
                    setLevelToRemove(null);
                    saveLevels(nextLevels);
                }}
                onCancel={() => setLevelToRemove(null)}
                isDestructive
                dataTestId={'level-remove-confirm'}
            />

            <PerformanceScoreLevelsSection />

            <EmployeePortalCompetenciesSection />
        </div>
    );
};
